import { nanoid } from 'nanoid';
import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { type Descendant } from 'slate';

import CustomLink from '@/components/CustomLink';
import ArrowElbowDownRight from '@/components/Icons/ArrowElbowDownRight';
import ProgressIndicator from '@/components/ProgressIndicator';
import { ConversationComposeBox } from '@/containers/chat-composeboxes/ConversationComposeBox';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import useNoodleApi from '@/hooks/useNoodleApi';
import { JobType, useJobContext } from '@/providers/Jobs';
import { JobStatus } from '@/providers/Jobs/Context';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { postNewMessage } from '@/tsClient';
import { MessageType } from '@/typings/api-models';

import s from './ThreadComment.module.scss';

type ConversationMessageInFlowThreadProps = PropsWithChildren<{
  messages?: {
    id: string;
    isDeleted?: boolean | null;
    parent?: {
      parent?: {
        id: string;
      };
    };
  }[];
  canReply: boolean;
  messageId: string;
  isThreadDetail?: boolean;
  isReadOnly?: boolean;
  creatorSlug: string;
  productSlug: string;
  replyInline?: boolean;
  isPostingMessage?: boolean;
  onPostNewMessage?: (comment: Awaited<ReturnType<typeof postNewMessage>>) => void;
  person?: {
    id: string;
    creator?: {
      slug: string;
    } | null;
  };
  sendAsCreator: boolean;
  isWorkflowComment?: boolean;
}>;

export const ConversationMessageInFlowThread: FC<ConversationMessageInFlowThreadProps> = ({
  messages,
  messageId,
  creatorSlug,
  isThreadDetail,
  canReply,
  children,
  isReadOnly,
  productSlug,
  replyInline = false,
  onPostNewMessage,
  person,
  sendAsCreator,
  isWorkflowComment,
}) => {
  const { fetchingState, getData: postCommentFn } = useNoodleApi(postNewMessage, {
    healthMonitor: { name: 'send-message' },
  });
  const [jobCorrelationId, setJobCorrelationId] = useState(nanoid());
  const { jobs, addJob } = useJobContext();
  const uploadJobs = jobs.filter((j) => j.correlationId === jobCorrelationId);
  const [isThreadOpen, setIsThreadOpen] = useState(false);
  const productCommentThreadUrl = getUrl(IDENTIFIERS.PRODUCT_COMMENT_THREAD, {
    commentId: messageId,
    creatorSlug,
    productSlug,
  });
  const WrapperComponent: FC<PropsWithChildren> = ({ children: wrapperChildren }) => (
    <CustomLink className={s['comment__start-btn']} to={productCommentThreadUrl}>
      {wrapperChildren}
    </CustomLink>
  );

  useEffect(() => {
    if (replyInline) {
      setIsThreadOpen(true);
    }
  }, [replyInline]);

  const handleSend = async ({
    text,
    medias = [],
    handbooks = [],
  }: {
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
  }): Promise<void> => {
    if (text?.length || medias?.length || handbooks?.length) {
      const newComment = await postCommentFn({
        creatorSlug,
        handbooks,
        medias,
        messageId,
        messageType: isWorkflowComment ? MessageType.WorkflowComment : MessageType.Comment,
        productSlug,
        sendAsCreator,
        text: text
          ? {
              children: text,
            }
          : null,
      });

      if (newComment?.data) {
        onPostNewMessage?.(newComment.data);
      }
      mixpanelTrack(creatorSlug === person?.creator?.slug ? '(Creator) Added reply' : 'Added reply', {
        medias,
        parentId: messageId,
        text,
      });
    }
  };

  const handleAsyncSend = async ({
    text,
    handbooks = [],
  }: {
    text?: { children: Descendant[] }[];
    handbooks?: { id: string }[];
  }): Promise<void> => {
    const sendFunction = async (overrideMediaIds?: { id: string }[]): Promise<void> => {
      await handleSend({
        handbooks,
        medias: overrideMediaIds,
        text,
      });
    };
    addJob({
      dependsOn: uploadJobs,
      function: sendFunction,
      metadata: {
        replyingTo: messageId,
      },
      title: 'Comment Reply',
      type: JobType.COMMENT_REPLY,
    });
    setJobCorrelationId(nanoid());
    setIsThreadOpen(false);
  };

  return (
    <div className={s.comment}>
      {jobs.find((j) => j.metadata?.replyingTo === messageId && j.status === JobStatus.IN_PROGRESS) && (
        <ProgressIndicator isCentered />
      )}
      {!!messages?.length && (messages.filter((c) => !c.isDeleted).length || 0) > 0 && (
        <>
          <div className={isThreadDetail ? s.comment__content__borderless : s.comment__content}>{children}</div>
        </>
      )}
      {canReply && !isReadOnly && !replyInline && !isThreadDetail && !isThreadOpen && (
        <WrapperComponent>
          <div className={s.replyButton}>
            <span className={s['comment__start-btn-icon']}>
              <ArrowElbowDownRight size={16} weight='fill' color='var(--color-gray-0)' />
            </span>
            Reply in thread
          </div>
        </WrapperComponent>
      )}
      {isThreadOpen &&
        canReply &&
        (creatorSlug === person?.creator?.slug ? (
          <ConversationComposeBox
            showAddVideo
            isLoomEnabled
            onSend={handleAsyncSend}
            showProTips={false}
            isFetching={false}
            placeholder='Type your reply'
            showAddHandbook
            isAsync
            jobCorrelationId={jobCorrelationId}
            referenceId={messageId}
            referenceType={'thread-comment'}
          />
        ) : (
          <ConversationComposeBox
            showAddVideo
            onSend={handleSend}
            showProTips={false}
            isFetching={fetchingState.isFetching}
            placeholder='Type your reply'
            showAddHandbook={person?.creator?.slug === creatorSlug}
            referenceId={messageId}
            referenceType={'thread-comment'}
            isLoomEnabled={person?.creator?.slug === creatorSlug}
          />
        ))}
    </div>
  );
};
